import { useEffect, useMemo, useRef, useState } from 'react';
import useLocales from 'src/hooks/useLocales';

// @mui
import {
  Box,
  Card,
  Table,
  Button,
  TableBody,
  Container,
  TableContainer,
  Grid,
  Stack,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  TableCell,
  Autocomplete,
  Chip,
  IconButton,
} from '@mui/material';

import { observer } from 'mobx-react-lite';

import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import Iconify from 'src/components/Iconify';

import useSettings from 'src/hooks/useSettings';

import { PATH_DASHBOARD } from 'src/routes/paths';
import Page from 'src/components/Page';
import { useStore } from 'src/stores/store';
import {
  IRentPaymentParams,
  IRentPaymentReport,
} from 'src/@types/Choqorak/MainTables/RentPayment.ts/RentPayment';

import { Controller, useForm } from 'react-hook-form';
import LocalizDatePicker from 'src/sections/common/LocalizDatePicker';
import Loader from 'src/components/loader/Loader';

import { GetMonthName } from 'src/sections/common/GetMonthName';
import { DateConverter } from 'src/sections/common/DateConverter';
import Label from 'src/components/Label';
import IncomeReportDetailsToolbar from './IncomeReportDetailsToolbar';
import { IncomeRepotParam } from 'src/@types/Choqorak/MainTables/Income/Income';
import * as XLSX from 'xlsx';
import { Icon } from '@iconify/react';
import { themeMode } from 'src/utils/general';
import { GetCurrencySymbol } from 'src/sections/common/GetCurrencySymbol';

// ----------------------------------------------------------------------
export default observer(function IncomeReportIndex() {
  const { IncomeStore, ChoqorakCommonDropdown } = useStore();
  const { translate } = useLocales();
  const { loadIncomeReport, IncomeReportResult } = IncomeStore;
  const { loadIncomeCategoryDDL, IncomeCategoryOption } = ChoqorakCommonDropdown;

  const { themeStretch } = useSettings();
  const [isloading, setIsloading] = useState(false);
  const [category, setCategory] = useState(); // State for the year

  const defaultValues = useMemo<IncomeRepotParam>(
    () => ({
      startDate: new Date(),
      endDate: new Date(),
    }),
    []
  );
  const methods = useForm<IncomeRepotParam>({
    defaultValues,
  });
  const { watch, control, setValue } = methods;
  const val = watch();

  useEffect(() => {
    loadIncomeCategoryDDL();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const Search = async (e: any) => {
    e.preventDefault();
    setIsloading(true);
    loadIncomeReport({
      startDate: val.startDate,
      endDate: val.endDate,
      incomeCategoryId: category,
    })
      .then((res) => {
        setIsloading(false);
      })
      .catch((err) => {
        setIsloading(false);
      });
  };

  const exportToExcel = () => {
    const data =
      IncomeReportResult?.list?.map((row, index) => ({
        No: index + 1,

        CarrencyName: row.carrencyType || '-',
        TotalAmount: row.amount ?? '-',
        IncomeCategoryName: row.incomeCategory || '-',
        description: row.description || '-',
      })) || [];

    if (data.length === 0) {
      alert('No data available to export.');
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Income Report');

    XLSX.writeFile(workbook, 'Income_Report.xlsx');
  };

  return (
    <Page title={translate('IncomeReport.Title')}>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading={translate('IncomeReport.IncomeReportTitle')}
          links={[
            { name: `${translate('Department.Dashboard')}`, href: PATH_DASHBOARD.root },

            { name: `${translate('IncomeReport.IncomeReportTitle')}` },
          ]}
          action={<></>}
        />

        <Grid
          container
          display="flex"
          alignItems="center"
          justifyContent="center" // Use justifyContent instead of justifyItems
        >
          <Grid item xs={12} md={12} spacing={2}>
            <Card
              sx={{
                p: 3,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center', // Use justifyContent instead of justifyItems
              }}
            >
              <Grid item xs={12} md={12}>
                <Box
                  sx={{
                    display: 'grid',
                    columnGap: 2,
                    rowGap: 3,
                    gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(6, 1fr)' },
                  }}
                >
                  <LocalizDatePicker
                    name="startDate"
                    label={translate('Income.FromDate')}
                    control={control}
                    size={'small'}
                  />

                  <LocalizDatePicker
                    name="endDate"
                    label={translate('Income.ToDate')}
                    control={control}
                    size={'small'}
                  />

                  <Controller
                    name="incomeCategoryId"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Autocomplete
                        fullWidth
                        freeSolo
                        onChange={(event, newValue) => setCategory(newValue.value)}
                        options={IncomeCategoryOption.map((option: any) => option)}
                        value={field.value}
                        getOptionLabel={(option) => option.text}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              key={option.value}
                              size="small"
                              label={option.value}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={translate('IncomeCategory.IncomeCategory')}
                            error={!!error}
                            size="small"
                            helperText={error?.message}
                          />
                        )}
                      />
                    )}
                  />

                  <Button
                    sx={{ width: '120px' }}
                    variant="contained"
                    onClick={Search}
                    startIcon={<Iconify icon="eva:search-fill" />}
                  >
                    {translate('ElectricityPayment.Filter')}
                  </Button>
                </Box>
              </Grid>
              <Grid
                item
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} // Use justifyContent instead of justifyItems
              >
                <IconButton
                  sx={{ color: themeMode() === 'light' ? 'black' : 'white' }}
                  onClick={exportToExcel}
                >
                  <Icon icon="vscode-icons:file-type-excel" width={24} height={24} />
                </IconButton>
              </Grid>
              <Grid
                item
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} // Use justifyContent instead of justifyItems
              >
                <IncomeReportDetailsToolbar
                  startDate={val.startDate}
                  endDate={val.endDate}
                  Report={IncomeReportResult} />
              </Grid>
            </Card>
          </Grid>
        </Grid>

        <Grid item xs={12} md={12} sx={{ mt: 2 }}>
          <>
            {isloading ? (
              <Loader />
            ) : (
              <>
                <Stack sx={{ p: 1 }}>
                  <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 700 }} aria-label="customized table">
                      <TableHead>
                        <TableRow>
                          <TableCell>{translate('GeneralFields.No')}</TableCell>
                          <TableCell>{translate('IncomeCategory.IncomeCategory')}</TableCell>
                          <TableCell>{translate('Income.Amount')}</TableCell>



                          <TableCell>{translate('Income.Date')}</TableCell>
                          <TableCell>{translate('Income.Description')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {IncomeReportResult?.list.map((row, index) => (
                          <TableRow key={index}>
                            <TableCell component="th" scope="row">
                              {index + 1}
                            </TableCell>
                            <TableCell align="left">
                              {' '}
                              <Label color="warning"> {row.incomeCategory}</Label>
                            </TableCell>

                            <TableCell align="left">
                              <Label color="success"> {row.amount}{' '}
                                {row.carrencyId === 2
                                  ? GetCurrencySymbol('afn')
                                  : GetCurrencySymbol('dollar')}</Label>
                            </TableCell>
                            <TableCell align="left">
                              {<DateConverter date={row.recieveDate} />}
                            </TableCell>
                            <TableCell align="left">
                              {' '}
                               {row.description}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              </>
            )}
          </>
        </Grid>
      </Container>
    </Page>
  );
});
